<template>
    <NuxtImg v-if="!lazyLoad" v-bind="$attrs" />
    <NuxtImg v-else-if="lazyLoad && intersecting" v-bind="$attrs" loading="lazy" />
    <div
        v-else
        ref="miscImageLazyPlaceholder"
        class="flex items-center justify-center"
        :class="$attrs.class"
        :style="placeholderStyle"
    >
        <img
            v-if="loading"
            src="/images/loader.gif"
            alt="Image is loading..."
            width="80"
            height="60"
            class="max-w-full max-h-full"
            data-not-lazy
        >
    </div>

</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'

defineOptions({
    inheritAttrs: false
})

const props = withDefaults(defineProps<{
    lazyLoad?: boolean
    loading?: boolean
}>(), {
    lazyLoad: false,
    loading: false
})

const attrs = useAttrs()

/**
 * Reserve the same box the final image will occupy so swapping in the
 * lazy-loaded image is a no-op (no layout shift). Uses the width/height
 * attrs for aspect-ratio when the consumer provides them; otherwise the
 * inherited sizing class (e.g. h-full w-full) constrains the placeholder.
 */
const placeholderStyle = computed(() => {
    const style: Record<string, string> = {}
    const w = parseFloat(String(attrs.width ?? ''))
    const h = parseFloat(String(attrs.height ?? ''))

    if (w > 0 && h > 0) {
        style.aspectRatio = `${w} / ${h}`
        style.width = '100%'
    }

    return style
})

const intersecting = ref(false)
const miscImageLazyPlaceholder = ref<null | HTMLElement>(null)

let observer = null
if (import.meta.client && props.lazyLoad) {
    observer = new IntersectionObserver((entries, self) => {
        for (const { isIntersecting, target } of entries) {
            if (isIntersecting) {
                intersecting.value = true
                self.unobserve(target)
            }
        }
    })
}

onMounted(() => {
    if (observer && miscImageLazyPlaceholder.value != null) observer.observe(miscImageLazyPlaceholder.value)
})

onUnmounted(() => {
    if (observer && miscImageLazyPlaceholder.value != null) observer.unobserve(miscImageLazyPlaceholder.value)
})
</script>
